import type {
  ActivityEntry,
  Comment,
  Evidence,
  EvidenceKind,
  ListResponse,
  TeamMember,
} from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'
import { useApiAction, type UseApiActionResult } from '@/api/useApiAction'

/**
 * The three child collections hanging off an initiative: comments,
 * evidence, team.
 *
 * One module because they are the same shape three times — a `{ data: [] }`
 * read plus a couple of writes against `/api/initiatives/{id}/<thing>` —
 * and splitting them into six files would only spread the pattern out.
 *
 * Each read takes a `refreshKey` so the detail view can force a re-fetch
 * after a write without the path changing.
 */

function unwrap<T>(query: QueryState<ListResponse<T>>): QueryState<T[]> {
  if (query.status === 'success') return { status: 'success', data: query.data.data }
  return query
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export function useComments(initiativeId: number, refreshKey = 0): QueryState<Comment[]> {
  return unwrap(
    useApiQuery<ListResponse<Comment>>(`/api/initiatives/${initiativeId}/comments`, refreshKey),
  )
}

export interface UseCommentActionsResult extends UseApiActionResult<Comment | null> {
  post: (initiativeId: number, body: string) => Promise<Comment | null>
  edit: (initiativeId: number, commentId: number, body: string) => Promise<Comment | null>
  remove: (initiativeId: number, commentId: number) => Promise<Comment | null>
}

export function useCommentActions(): UseCommentActionsResult {
  const action = useApiAction<Comment | null>()
  return {
    ...action,
    post: (initiativeId, body) =>
      action.run(`/api/initiatives/${initiativeId}/comments`, 'POST', { body }),
    edit: (initiativeId, commentId, body) =>
      action.run(`/api/initiatives/${initiativeId}/comments/${commentId}`, 'PATCH', { body }),
    // 204, so `apiFetch` resolves undefined and the action stores null.
    // Callers check `state.status === 'success'`, not the payload.
    remove: (initiativeId, commentId) =>
      action.run(`/api/initiatives/${initiativeId}/comments/${commentId}`, 'DELETE'),
  }
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export function useEvidence(initiativeId: number, refreshKey = 0): QueryState<Evidence[]> {
  return unwrap(
    useApiQuery<ListResponse<Evidence>>(`/api/initiatives/${initiativeId}/evidence`, refreshKey),
  )
}

export interface AddEvidenceBody {
  evidenceKind: EvidenceKind
  title: string
  evidenceUrl?: string
  note?: string
}

export interface UseEvidenceActionsResult extends UseApiActionResult<Evidence | null> {
  add: (initiativeId: number, body: AddEvidenceBody) => Promise<Evidence | null>
  remove: (initiativeId: number, evidenceId: number) => Promise<Evidence | null>
}

export function useEvidenceActions(): UseEvidenceActionsResult {
  const action = useApiAction<Evidence | null>()
  return {
    ...action,
    add: (initiativeId, body) =>
      action.run(`/api/initiatives/${initiativeId}/evidence`, 'POST', body),
    remove: (initiativeId, evidenceId) =>
      action.run(`/api/initiatives/${initiativeId}/evidence/${evidenceId}`, 'DELETE'),
  }
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

/** The API's ceiling on `limit`, per docs/api-contracts.md. */
export const ACTIVITY_LIMIT_MAX = 200

/**
 * The merged activity stream, newest first, `limit` entries at a time.
 *
 * The detail response carries an `activity` array of its own, but the feed on
 * the page is opened on request and grown a few entries at a time — so it
 * reads the dedicated endpoint, where the size of the page is the caller's
 * choice. Mount the component that calls this only once the reader has asked
 * for it: that is what makes the fetch lazy.
 */
export function useActivity(
  initiativeId: number,
  limit: number,
  refreshKey = 0,
): QueryState<ActivityEntry[]> {
  return unwrap(
    useApiQuery<ListResponse<ActivityEntry>>(
      `/api/initiatives/${initiativeId}/activity?limit=${Math.min(limit, ACTIVITY_LIMIT_MAX)}`,
      refreshKey,
    ),
  )
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

export function useTeam(initiativeId: number, refreshKey = 0): QueryState<TeamMember[]> {
  return unwrap(
    useApiQuery<ListResponse<TeamMember>>(`/api/initiatives/${initiativeId}/team`, refreshKey),
  )
}

export interface UseTeamActionsResult extends UseApiActionResult<TeamMember | null> {
  add: (
    initiativeId: number,
    entraObjectId: string,
    roleNote?: string,
  ) => Promise<TeamMember | null>
  remove: (initiativeId: number, memberId: number) => Promise<TeamMember | null>
}

export function useTeamActions(): UseTeamActionsResult {
  const action = useApiAction<TeamMember | null>()
  return {
    ...action,
    add: (initiativeId, entraObjectId, roleNote) =>
      action.run(`/api/initiatives/${initiativeId}/team`, 'POST', {
        entraObjectId,
        ...(roleNote ? { roleNote } : {}),
      }),
    remove: (initiativeId, memberId) =>
      action.run(`/api/initiatives/${initiativeId}/team/${memberId}`, 'DELETE'),
  }
}
